import { buildCostLotPickerSections, buildLotCostGroups, lotGroupSelectionValue, parseLotGroupSelection } from './lots/lotCostGroups'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'

import ContextSelectorBar, { type SelectorOption } from '../components/ContextSelectorBar'
import CurrentContextBar from '../components/CurrentContextBar'
import { ensureFarmList, type WizardFarm } from '../utils/farmWizard'
import { findFarmByIdOrName, findProductionUnitByIdOrName } from '../utils/contextSelection'
import { loadActiveContextSelection, persistActiveContextSelection } from '../utils/activeContextSelection'
import { readScopedString, writeScopedString } from '../utils/scopedStorage'
import { useStoredPrincipal } from '../hooks/useStoredPrincipal'
import { useLots } from '../hooks/useLots'
import { isLotActiveForInteraction } from '../utils/lotInteraction'
import { useT } from '../i18n'
import { FACILITY_COST_SELECTION, LotCostSection } from './lots/LotCostSection'
import { readFacilityCurrency } from '../utils/facilityCurrency'
import { resolveIncomingLotId } from './costs/urlSearchParams'

/**
 * Costs screen — dedicated log surface for the `LotCostEvent` Tier A stream.
 *
 * v2.2 T3-C-07 (LCE-01..LCE-06). Mirrors the Water screen shell so the
 * peer-alignment story stays intact: responsive-container → header →
 * `<CurrentContextBar>` → `<ContextSelectorBar>` (facility + PU + lot) →
 * primary body (`<LotCostSection>` in standalone mode).
 *
 * The screen owns only selection state (facility / PU / lot). All storage,
 * sync, and form behavior lives inside `LotCostSection` and the
 * `LotCostEvent` Tier A pipeline (server + Dexie + entity outbox), which
 * are NOT touched by the screen extraction.
 */
export default function Costs() {
  const t = useT()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const principal = useStoredPrincipal()
  // v2.2 T3-C-07: cost writes reuse the "edit lots" permission for the
  // initial extraction. A separate `canEditCosts` RBAC gate is a future
  // card (see docs/lot-cost-screen-implementation-plan.md risks table).
  const canEditCosts = !principal || principal.role === 'editor' || principal.role === 'admin'

  const deepLink = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return {
      farmId: params.get('farmId')?.trim() || '',
      farmName: params.get('farmName')?.trim() || '',
      productionUnitId: params.get('productionUnitId')?.trim() || '',
      productionUnitName: params.get('productionUnitName')?.trim() || ''
    }
  }, [location.search])

  const [availableFarms, setAvailableFarms] = useState<WizardFarm[]>([])
  const [selectedFarmId, setSelectedFarmId] = useState<string>('')
  const [selectedProductionUnitId, setSelectedProductionUnitId] = useState<string>('')
  const [selectedLotId, setSelectedLotId] = useState<string>('')

  // Load the facility list once (mirrors Water). Deep-link params override
  // the saved selection; otherwise fall back to the tenant's active context.
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const farms = await ensureFarmList()
      if (cancelled) return
      setAvailableFarms(farms)

      const savedFarmToken = readScopedString('costs-selected-farm')
      const savedUnitToken = readScopedString('costs-selected-production-unit')
      const activeContext = loadActiveContextSelection()

      const farmToken = deepLink.farmId || deepLink.farmName || activeContext.farmId || savedFarmToken || ''
      const resolvedFarm = findFarmByIdOrName(farms, farmToken)
      if (resolvedFarm?.id) {
        setSelectedFarmId(String(resolvedFarm.id))
      } else if (farms.length > 0) {
        setSelectedFarmId(String(farms[0].id))
      }

      const unitToken = deepLink.productionUnitId || deepLink.productionUnitName || activeContext.productionUnitId || savedUnitToken || ''
      const resolvedUnit = findProductionUnitByIdOrName(resolvedFarm ?? farms[0], unitToken)
      if (resolvedUnit?.id) {
        setSelectedProductionUnitId(String(resolvedUnit.id))
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [deepLink.farmId, deepLink.farmName, deepLink.productionUnitId, deepLink.productionUnitName])

  const selectedFarm = useMemo(
    () => availableFarms.find((farm) => String(farm?.id ?? '') === selectedFarmId),
    [availableFarms, selectedFarmId]
  )

  const availableProductionUnits = useMemo(() => selectedFarm?.productionUnits ?? [], [selectedFarm])

  // Keep the persisted selection tokens in sync with the current picks so
  // reopening the screen lands where the operator left off.
  useEffect(() => {
    if (selectedFarmId) {
      writeScopedString('costs-selected-farm', selectedFarmId)
      persistActiveContextSelection({ farmId: selectedFarmId })
    }
  }, [selectedFarmId])

  useEffect(() => {
    if (selectedProductionUnitId) {
      writeScopedString('costs-selected-production-unit', selectedProductionUnitId)
    }
  }, [selectedProductionUnitId])

  const { lots: availableLots } = useLots({
    farmId: selectedFarmId || undefined,
    productionUnitId: selectedProductionUnitId || undefined,
    enabled: Boolean(selectedFarmId)
  })

  // Findings #131: lots sown into several units, offered as "todo el lote".
  // Built from the whole facility's lots (any unit filter only narrows which
  // groups are listed: those with a unit in the filter).
  const { lots: facilityLots } = useLots({
    farmId: selectedFarmId || undefined,
    enabled: Boolean(selectedFarmId)
  })
  const lotGroups = useMemo(
    () => buildLotCostGroups(facilityLots.filter((lot) => lot.farmId === selectedFarmId)),
    [facilityLots, selectedFarmId]
  )
  const visibleLotGroups = useMemo(
    () => lotGroups.filter((group) => !selectedProductionUnitId || group.members.some((member) => member.productionUnitId === selectedProductionUnitId)),
    [lotGroups, selectedProductionUnitId]
  )
  const unitNameById = useMemo(
    () => new Map(availableProductionUnits.map((unit) => [String(unit.id ?? ''), String(unit.name ?? unit.id ?? '')] as const)),
    [availableProductionUnits]
  )
  // "Registrar costo de las ovas" (after Sembrar en varias unidades):
  // ?lotGroup=<root>&costCategory=G&costDate=YYYY-MM-DD
  const groupDeepLink = useMemo(() => {
    const rootId = searchParams.get('lotGroup')?.trim() || ''
    const category = searchParams.get('costCategory')?.trim() || ''
    const date = searchParams.get('costDate')?.trim() || ''
    return rootId ? { rootId, category, date } : null
  }, [searchParams])
  useEffect(() => {
    if (!groupDeepLink) return
    if (lotGroups.some((group) => group.rootId === groupDeepLink.rootId)) {
      setSelectedLotId(lotGroupSelectionValue(groupDeepLink.rootId))
    }
  }, [groupDeepLink, lotGroups])
  const costPrefill = useMemo(() => {
    if (!groupDeepLink || selectedLotId !== lotGroupSelectionValue(groupDeepLink.rootId)) return undefined
    const categoryPrefix = (['G', 'H', 'L'] as const).find((prefix) => prefix === groupDeepLink.category) ?? 'G'
    return { categoryPrefix, recordedAt: /^\d{4}-\d{2}-\d{2}/.test(groupDeepLink.date) ? groupDeepLink.date : undefined }
  }, [groupDeepLink, selectedLotId])

  // Scoped view of the lots relevant to the current facility+PU. Mirrors the
  // filter `LotCostSection` used internally when it received `lots` as a
  // prop — kept here so the deep-link resolver + selector list see the same
  // shape.
  const scopedLots = useMemo(() => {
    return availableLots
      .filter((lot) => !selectedFarmId || lot.farmId === selectedFarmId)
      .filter((lot) => !selectedProductionUnitId || lot.productionUnitId === selectedProductionUnitId)
      .sort((a, b) => (a.lotKey || '').localeCompare(b.lotKey || ''))
  }, [availableLots, selectedFarmId, selectedProductionUnitId])

  // v2.2 T3-C-07 (LCE-05): deep-link `?lotId=…` seeds the lot selector when
  // the referenced lot is in the current scope. If the lot isn't in scope
  // (wrong facility/PU, or unknown id), clear any previous selection rather
  // than pinning the operator to a lot they can't see.
  useEffect(() => {
    const incoming = resolveIncomingLotId(searchParams, scopedLots)
    if (incoming && incoming !== selectedLotId) {
      setSelectedLotId(incoming)
      return
    }
    if (searchParams.get('lotId') && !incoming && selectedLotId) {
      // A `?lotId=` param was requested but the target isn't in scope.
      // Only clear when we know the scope has resolved (avoid clobbering
      // during first-load races before `useLots` returns anything).
      if (scopedLots.length > 0 || !selectedFarmId) {
        setSelectedLotId('')
      }
    }
  }, [searchParams, scopedLots, selectedLotId, selectedFarmId])

  // If the operator switches facility/PU and the current lot no longer
  // belongs to the visible scope, drop it. Matches the intent of Water's
  // `setAvailableLots([])` on context switch.
  useEffect(() => {
    if (!selectedLotId || selectedLotId === FACILITY_COST_SELECTION) return
    const groupRoot = parseLotGroupSelection(selectedLotId)
    if (groupRoot) {
      if (lotGroups.length > 0 && !visibleLotGroups.some((group) => group.rootId === groupRoot)) setSelectedLotId('')
      return
    }
    if (!scopedLots.some((lot) => lot.id === selectedLotId)) {
      setSelectedLotId('')
    }
  }, [scopedLots, selectedLotId, lotGroups, visibleLotGroups])

  // Findings #149: one section per lot ("todo el lote", then each unit), no arrival record.
  const lotOptions = useMemo((): SelectorOption[] => {
    const sections = buildCostLotPickerSections(scopedLots, visibleLotGroups, (unitId) => unitNameById.get(unitId) ?? '')
    return sections.flatMap((section) => {
      const groupLotIds = new Set(section.group?.members.map((member) => member.lotId) ?? [])
      const groupOption: SelectorOption[] = section.group
        ? [{
            key: lotGroupSelectionValue(section.group.rootId),
            value: lotGroupSelectionValue(section.group.rootId),
            label: t('lots.costs.group.option', { lot: section.lotKey, count: section.group.members.length }),
            group: section.lotKey
          }]
        : []
      return [...groupOption, ...section.lots.map((lot) => {
        const lotKey = lot.lotKey || `Lot ${lot.id}`
        const unit = unitNameById.get((lot.productionUnitId ?? '').trim()) ?? ''
        const base = !unit
          ? lotKey
          : groupLotIds.has(lot.id)
            ? t('lots.costs.group.unitOption', { lot: lotKey, unit })
            : `${lotKey} · ${unit}`
        const active = isLotActiveForInteraction(lot.stage)
        return {
          key: lot.id,
          value: lot.id,
          label: active ? base : `${base} (${t('lots.stage.inactive')})`,
          disabled: !active,
          group: section.lotKey
        }
      })]
    })
  }, [scopedLots, visibleLotGroups, unitNameById, t])

  const selectedFarmLabel = useMemo(() => {
    if (!selectedFarmId) return t('costs.placeholder.selectFacility')
    const farm = availableFarms.find((entry) => String(entry?.id ?? '') === selectedFarmId)
    return farm?.name ?? selectedFarmId
  }, [availableFarms, selectedFarmId, t])

  const selectedProductionUnitLabel = useMemo(() => {
    if (!selectedProductionUnitId) return '—'
    const unit = availableProductionUnits.find((entry) => String(entry?.id ?? '') === selectedProductionUnitId)
    return unit?.name ?? selectedProductionUnitId
  }, [availableProductionUnits, selectedProductionUnitId])

  const selectedLotLabel = useMemo(() => {
    if (!selectedLotId) return '—'
    if (selectedLotId === FACILITY_COST_SELECTION) return t('costs.facilityWide.option')
    const groupRoot = parseLotGroupSelection(selectedLotId)
    const group = groupRoot ? lotGroups.find((entry) => entry.rootId === groupRoot) : undefined
    if (group) return t('lots.costs.group.option', { lot: group.lotKey, count: group.members.length })
    const option = lotOptions.find((entry) => entry.value === selectedLotId)
    if (option) return option.label
    const lot = scopedLots.find((entry) => entry.id === selectedLotId)
    return lot?.lotKey || lot?.id || selectedLotId
  }, [scopedLots, selectedLotId, lotGroups, lotOptions, t])

  return (
    <div className="responsive-container">
      <h2 style={{ marginTop: 0 }}>{t('costs.title')}</h2>
      {!canEditCosts && (
        <p style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>
          {t('costs.readonly.banner')}
        </p>
      )}
      <p style={{ marginTop: 0, fontSize: 13, color: '#475569' }}>{t('costs.subtitle')}</p>

      <CurrentContextBar
        fields={[
          { label: t('costs.context.facility'), value: selectedFarmLabel },
          { label: t('costs.context.productionUnit'), value: selectedProductionUnitLabel },
          { label: t('costs.context.lot'), value: selectedLotLabel }
        ]}
      />

      <div className="responsive-toolbar">
        <ContextSelectorBar
          farm={{
            label: t('costs.context.facility'),
            value: selectedFarmId,
            onChange: (value) => {
              setSelectedFarmId(value)
              setSelectedProductionUnitId('')
              setSelectedLotId('')
            },
            placeholder: t('costs.placeholder.selectFacility'),
            options: availableFarms.map((farm) => ({
              key: farm.id ?? farm.name,
              value: String(farm.id ?? ''),
              label: farm.name
            }))
          }}
          productionUnit={{
            label: t('costs.context.productionUnit'),
            value: selectedProductionUnitId,
            onChange: (value) => {
              setSelectedProductionUnitId(value)
              setSelectedLotId('')
            },
            disabled: !selectedFarmId || availableProductionUnits.length === 0,
            placeholder: !selectedFarmId
              ? t('costs.placeholder.selectFacilityFirst')
              : t('costs.placeholder.selectProductionUnit'),
            options: availableProductionUnits.map((unit) => ({
              key: unit.id ?? unit.name,
              value: String(unit.id ?? ''),
              label: unit.name
            }))
          }}
          lot={{
            label: t('costs.context.lot'),
            value: selectedLotId,
            onChange: (value) => setSelectedLotId(value),
            disabled: !selectedFarmId,
            placeholder: !selectedFarmId
              ? t('costs.placeholder.selectFacilityFirst')
              : t('costs.placeholder.selectLot'),
            // findings #100: facility-wide costs (no lot), summed, never allocated.
            options: [{
              key: FACILITY_COST_SELECTION,
              value: FACILITY_COST_SELECTION,
              label: t('costs.facilityWide.option')
            }, ...lotOptions]
          }}
          summary={null}
        />
      </div>

      {selectedFarmId ? (
        <LotCostSection
          farmId={selectedFarmId}
          productionUnitId={selectedProductionUnitId || undefined}
          lots={scopedLots}
          canEdit={canEditCosts}
          selectedLotId={selectedLotId}
          onSelectLot={setSelectedLotId}
          facilityCurrency={readFacilityCurrency(selectedFarm) ?? undefined}
          lotGroups={lotGroups}
          unitNameById={unitNameById}
          prefill={costPrefill}
        />
      ) : (
        <p style={{ marginTop: 16, fontSize: 13, color: '#64748b' }}>
          {t('costs.placeholder.selectFacility')}
        </p>
      )}
    </div>
  )
}
